import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { ProblemBadge } from "@/components/ProblemBadge";
import { useOptionalPracticeContext } from "@/hooks/usePracticeContext";
import { api, authHeaders } from "@/lib/api";
import { isOpenProblem } from "@/lib/callProblem";
import { CLOSURE_LABEL, inferClosureKind, type ClosureKind } from "@/lib/closure";
import { type CallSummary, type CallStatus } from "@/lib/types";

/** Allowed page sizes — mirrored on the server (see ApiHandler.AllowedPageSizes). */
const PAGE_SIZES = [20, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_STORAGE_KEY = "hlasemmy.callsPageSize";

const readStoredPageSize = (): number => {
  try {
    const raw = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    return (PAGE_SIZES as readonly number[]).includes(n) ? n : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
};

export const Calls = () => {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const practices = useQuery({ queryKey: ["practices"], queryFn: api.listPractices });
  // Available only inside DoctorApp; null in AdminApp. When non-null, drives the doctor's default
  // practice selection here so /calls follows the practice the doctor has picked in the global combobox.
  const practiceCtx = useOptionalPracticeContext();
  const activePracticeId = practiceCtx?.activePractice?.id;

  const isAdmin = me.data?.kind === "admin";
  const defaults = useMemo(defaultDateRange, []);
  // Empty string == "all practices" for admin; doctor users always have a concrete id selected.
  const [practiceId, setPracticeId] = useState<string>("");
  const [from, setFrom] = useState<string>(defaults.fromLocal);
  const [to, setTo] = useState<string>(defaults.toLocal);
  const [pageSize, setPageSize] = useState<number>(readStoredPageSize);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [onlyProblems, setOnlyProblems] = useState<boolean>(false);

  // Persist page-size choice across sessions. Admins / doctors typically settle on a preferred density.
  useEffect(() => {
    try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
    } catch {
      /* storage unavailable; not a blocker */
    }
  }, [pageSize]);

  // Doctor: default to the globally-active practice from context (combobox-driven) if available, else
  // first accessible practice in the API list. Admin: keep "all" by default.
  const effectivePracticeId = !isAdmin && !practiceId
    ? activePracticeId ?? practices.data?.[0]?.id ?? ""
    : practiceId;

  const showPracticeColumn = isAdmin && effectivePracticeId === "";

  // Reset to the first page whenever the filter changes — paging through stale results would be
  // confusing (page 5 of an old query → page 1 of a much smaller new one).
  useEffect(() => {
    setPageIndex(0);
  }, [effectivePracticeId, from, to, pageSize, onlyProblems]);

  const offset = pageIndex * pageSize;
  const practiceFilter = effectivePracticeId.length > 0 ? effectivePracticeId : null;
  const callsQuery = useQuery({
    queryKey: ["calls", effectivePracticeId, from, to, pageSize, offset, onlyProblems],
    queryFn: () =>
      onlyProblems
        ? api.listOpenProblemCalls(practiceFilter, pageSize, offset)
        : api.listCalls(
            practiceFilter,
            new Date(from).toISOString(),
            new Date(to).toISOString(),
            pageSize,
            offset,
          ),
    enabled:
      // Doctor: requires a practice selected. Admin: always enabled (server lists across all when
      // practiceId is empty).
      isAdmin || effectivePracticeId.length > 0,
  });

  const problemsCount = useQuery({
    queryKey: ["call-problems-count", effectivePracticeId],
    queryFn: () => api.countOpenProblems(practiceFilter),
    enabled: isAdmin,
  });

  const practiceNameById = useMemo(() => {
    const m = new Map<string, string>();
    practices.data?.forEach((p) => m.set(p.id, p.displayName));
    return m;
  }, [practices.data]);

  const total = callsQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const downloadCsv = async () => {
    const url = api.callsCsvUrl(
      effectivePracticeId.length > 0 ? effectivePracticeId : null,
      new Date(from).toISOString(),
      new Date(to).toISOString(),
    );
    const headers = await authHeaders();
    // Bearer auth via fetch + blob → object URL → anchor click. We can't put the bearer token in a plain
    // <a href>, so this dance is required to trigger the Save-As dialog with auth.
    const resp = await fetch(url, { headers });
    if (!resp.ok) {
      alert(`Stažení selhalo: HTTP ${resp.status}`);
      return;
    }
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filenameFromContentDisposition(resp.headers.get("Content-Disposition")) ?? "calls.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  };

  return (
    <div className="mx-auto max-w-6xl p-8">
      <h2 className="mb-6 text-2xl tracking-tight text-[#1a2b6b]">Hovory</h2>

      <div className="mb-6 flex flex-wrap items-end gap-4 rounded border border-gray-200 bg-white p-4">
        {/* Practice dropdown is admin-only — doctors switch via the global combobox in the nav. */}
        {isAdmin && (
          <label className="flex flex-col text-xs font-medium uppercase tracking-wider text-gray-600">
            Ordinace
            <select
              value={effectivePracticeId}
              onChange={(e) => setPracticeId(e.target.value)}
              className="mt-1 min-w-[12rem] rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-[#1a2b6b] focus:outline-none"
              disabled={practices.isPending}
            >
              <option value="">Všechny ordinace</option>
              {practices.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="flex flex-col text-xs font-medium uppercase tracking-wider text-gray-600">
          Od
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            disabled={onlyProblems}
            className="mt-1 rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
          />
        </label>
        <label className="flex flex-col text-xs font-medium uppercase tracking-wider text-gray-600">
          Do
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            disabled={onlyProblems}
            className="mt-1 rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
          />
        </label>
        {onlyProblems && <p className="self-center text-xs text-gray-500">bez ohledu na datum</p>}
        <button
          type="button"
          onClick={downloadCsv}
          disabled={onlyProblems || !callsQuery.isSuccess || total === 0}
          className="ml-auto rounded border border-gray-300 px-3 py-2 text-sm text-[#1a2b6b] hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
          title="Stáhnout výsledky pro zvolené období jako CSV"
        >
          Stáhnout CSV
        </button>
      </div>

      {isAdmin && (
        <div className="mb-4 flex gap-2">
          <FilterChip active={!onlyProblems} onClick={() => setOnlyProblems(false)}>
            Vše
          </FilterChip>
          <FilterChip active={onlyProblems} onClick={() => setOnlyProblems(true)}>
            Problémy
            {(problemsCount.data?.open ?? 0) > 0 && (
              <span className="ml-2 rounded-full bg-red-600 px-1.5 text-xs text-white">
                {problemsCount.data?.open}
              </span>
            )}
          </FilterChip>
        </div>
      )}

      {!isAdmin && !effectivePracticeId && practices.isSuccess && (
        <p className="text-gray-500">Nejprve vytvořte ordinaci pro zobrazení hovorů.</p>
      )}
      {callsQuery.isPending && (isAdmin || effectivePracticeId) && (
        <p className="text-gray-500">Načítám…</p>
      )}
      {callsQuery.isError && (
        <p className="text-red-600">Chyba: {(callsQuery.error as Error).message}</p>
      )}
      {callsQuery.isSuccess && callsQuery.data.rows.length === 0 && (
        <p className="text-gray-500">
          {onlyProblems ? "Žádné nevyřešené problémy." : "V tomto období nejsou žádné hovory."}
        </p>
      )}
      {callsQuery.isSuccess && callsQuery.data.rows.length > 0 && (
        <>
          <CallsTable
            calls={callsQuery.data.rows}
            showPracticeColumn={showPracticeColumn}
            practiceNameById={practiceNameById}
          />
          <PaginationFooter
            total={total}
            pageIndex={pageIndex}
            pageSize={pageSize}
            pageCount={pageCount}
            onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
            onNext={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}
            onPageSizeChange={setPageSize}
          />
        </>
      )}
    </div>
  );
};

const PaginationFooter = ({
  total,
  pageIndex,
  pageSize,
  pageCount,
  onPrev,
  onNext,
  onPageSizeChange,
}: {
  total: number;
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  onPrev: () => void;
  onNext: () => void;
  onPageSizeChange: (n: number) => void;
}) => {
  const firstRow = total === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow = Math.min(total, (pageIndex + 1) * pageSize);
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-4 text-sm text-gray-600">
      <div>
        Zobrazeno <b>{firstRow}–{lastRow}</b> z <b>{total}</b>
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500">
          Na stránku
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-[#1a2b6b] focus:outline-none"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={onPrev}
          disabled={pageIndex === 0}
          className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
        >
          ← Předchozí
        </button>
        <span className="font-mono text-xs text-gray-500">
          {pageIndex + 1} / {pageCount}
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={pageIndex >= pageCount - 1}
          className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
        >
          Další →
        </button>
      </div>
    </div>
  );
};

const CallsTable = ({
  calls,
  showPracticeColumn,
  practiceNameById,
}: {
  calls: CallSummary[];
  showPracticeColumn: boolean;
  practiceNameById: Map<string, string>;
}) => (
  <div className="overflow-x-auto rounded border border-gray-200 bg-white">
    <table className="w-full text-sm">
      <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
        <tr>
          <th className="px-4 py-3 font-medium">Zahájen</th>
          {showPracticeColumn && <th className="px-4 py-3 font-medium">Ordinace</th>}
          <th className="px-4 py-3 font-medium">Stav</th>
          <th className="px-4 py-3 font-medium">Volající</th>
          <th className="px-4 py-3 font-medium">Typ požadavku</th>
          <th className="px-4 py-3 font-medium">Shrnutí</th>
          <th className="px-4 py-3" />
        </tr>
      </thead>
      <tbody>
        {calls.map((c) => {
          const closureKind = inferClosureKind(c);
          return (
            <tr
              key={c.id}
              className={
                "border-b border-gray-100 last:border-b-0 hover:bg-gray-50 " +
                (closureKind ? "text-gray-500 italic" : "")
              }
            >
              <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-600">
                {formatDate(c.startedAt)}
              </td>
              {showPracticeColumn && (
                <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                  {practiceNameById.get(c.practiceId) ?? c.practiceId}
                </td>
              )}
              <td className="whitespace-nowrap px-4 py-3">
                {closureKind ? (
                  <ClosureBadge kind={closureKind} />
                ) : isOpenProblem(c.problem) ? (
                  <ProblemBadge problem={c.problem} />
                ) : (
                  <StatusBadge status={c.status} />
                )}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-gray-700">
                {c.callerPhone ?? "—"}
              </td>
              {/* For closure calls, request-type + summary are meaningless (the post-call pipeline doesn't
                  run), so we collapse them into a single "wasn't answered" cell that spans both columns. */}
              {closureKind ? (
                <td colSpan={2} className="px-4 py-3 text-gray-500">
                  Hovor nebyl přijat — Hlas Emmy přehrál uzavírkovou hlášku.
                </td>
              ) : (
                <>
                  <td className="px-4 py-3 text-gray-700">{c.requestTypeId ?? "—"}</td>
                  <td className="min-w-[16rem] px-4 py-3 text-gray-700">
                    {c.summary130Char ?? "—"}
                  </td>
                </>
              )}
              <td className="whitespace-nowrap px-4 py-3 text-right">
                <Link to={`/calls/${c.id}`} className="text-sm text-[#1a2b6b] hover:underline">
                  Detail →
                </Link>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const STATUS_STYLE: Record<CallStatus, string> = {
  in_progress:              "bg-blue-50 text-blue-700",
  awaiting_post_processing: "bg-amber-50 text-amber-700",
  done:                     "bg-green-50 text-green-700",
  failed:                   "bg-red-50 text-red-700",
};

const STATUS_LABEL: Record<CallStatus, string> = {
  in_progress:              "Probíhá",
  awaiting_post_processing: "Zpracování",
  done:                     "Dokončeno",
  failed:                   "Selhalo",
};

const StatusBadge = ({ status }: { status: CallStatus }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[status] ?? "bg-gray-100 text-gray-700"}`}
  >
    {STATUS_LABEL[status] ?? status}
  </span>
);

const FilterChip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={
      "flex items-center rounded-full border px-3 py-1 text-sm " +
      (active
        ? "border-[#1a2b6b] bg-[#1a2b6b] text-white"
        : "border-gray-300 bg-white text-[#1a2b6b] hover:bg-gray-50")
    }
  >
    {children}
  </button>
);

const ClosureBadge = ({ kind }: { kind: ClosureKind }) => (
  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
    {CLOSURE_LABEL[kind]}
  </span>
);

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

/** Default the date range to "last 7 days" in the browser's local zone, formatted for `<input type="datetime-local">`. */
const defaultDateRange = (): { fromLocal: string; toLocal: string } => {
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  return { fromLocal: toLocalInput(weekAgo), toLocal: toLocalInput(now) };
};

const toLocalInput = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Pull `filename="..."` out of a Content-Disposition header. Returns null when missing or malformed. */
const filenameFromContentDisposition = (cd: string | null): string | null => {
  if (!cd) return null;
  const match = cd.match(/filename="?([^"]+)"?/);
  return match ? match[1] : null;
};
